import React, { useState, useEffect } from 'react';
import { Table, Space, Button, Tag, Tooltip, Modal } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { PageTitle } from '@/shared/components/PageTitle';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/shared/lib/auth-context';
import { useGetApiAdminGuides, useDeleteApiAdminGuidesGuideId } from '@/features/guides/api/generated';
import type { Guide, EngineType, VehicleFilter, GetApiAdminGuidesSort } from '@/features/guides/api/generated';
import { useNotification } from '@/shared/hooks/useNotification';
import { useTranslation } from 'react-i18next';
import { FilterPills } from '../components/FilterPills';


interface GuidesTableData extends Guide {
  key: string;
}

type SortField = 'status' | 'createdAt' | 'updatedAt';
type SortOrder = 'asc' | 'desc';

export const GuidesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const { contextHolder, notification } = useNotification();
  const { t, i18n } = useTranslation();

  // Initialize state from URL params (only runs once)
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialPageSize = Number(searchParams.get('pageSize')) || 100;
  const initialSortField = (searchParams.get('sortField') as SortField) || 'updatedAt';
  const initialSortOrder = (searchParams.get('sortOrder') as SortOrder) || 'desc';

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortField, setSortField] = useState<SortField | null>(initialSortField);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
  const [solutionsModalOpen, setSolutionsModalOpen] = useState(false);
  const [solutionsModalData, setSolutionsModalData] = useState<{ problem: string; solutions: string[] } | null>(null);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [filtersModalData, setFiltersModalData] = useState<VehicleFilter[]>([]);

  // Update URL when state changes (only if different from defaults)
  useEffect(() => {
    const newParams = new URLSearchParams();

    // Only add params if they differ from defaults
    if (currentPage !== 1) {
      newParams.set('page', String(currentPage));
    }
    if (pageSize !== 100) {
      newParams.set('pageSize', String(pageSize));
    }
    if (sortField !== 'updatedAt' || sortOrder !== 'desc') {
      if (sortField) {
        newParams.set('sortField', sortField);
        newParams.set('sortOrder', sortOrder);
      }
    }

    // Only update if params actually changed
    if (searchParams.toString() !== newParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [currentPage, pageSize, sortField, sortOrder, searchParams, setSearchParams]);

  // Build sort parameter according to API spec: "field:direction"
  const sortParam = sortField ? `${sortField}:${sortOrder}` as GetApiAdminGuidesSort : undefined;

  const {
    data: guidesResponse,
    isLoading,
    refetch,
  } = useGetApiAdminGuides(
    {
      Page: currentPage,
      PageSize: pageSize,
      ...(sortParam && { Sort: sortParam }),
    },
    {
      query: {
        enabled: !!token && isAuthenticated && !authLoading,
      },
    }
  );

  const deleteGuideMutation = useDeleteApiAdminGuidesGuideId();

  const handleDeleteGuide = (guideId: string) => {
    Modal.confirm({
      title: t('guides.deleteGuide'),
      content: t('guides.deleteConfirm'),
      okText: t('common.delete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await deleteGuideMutation.mutateAsync({ guideId });
          notification.success(t('guides.deleteSuccess'));
          // Wait 500ms before refetching to allow backend to process deletion
          setTimeout(async () => {
            await refetch();
          }, 500);
        } catch (error) {
          notification.error(t('guides.deleteError'));
        }
      },
    });
  };

  const getEngineTypeColor = (engineType: EngineType): string => {
    const colors: Record<EngineType, string> = {
      'ICE': 'blue',
      'BEV': 'green',
      'PHEV': 'orange',
      'HEV': 'cyan',
      'REX': 'purple',
      'Unknown': 'default',
    };
    return colors[engineType] || 'default';
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      'Published': 'green',
      'Draft': 'orange',
      'Archived': 'red',
    };
    return colors[status] || 'default';
  };

  const columns: ColumnsType<GuidesTableData> = [
    {
      title: t('guides.problem'),
      dataIndex: ['content', 'problem'],
      key: 'problem',
      width: 500,
      render: (_: string, record) => {
        // Use translatedToEnglish if locale is English, otherwise use content
        const isEnglish = i18n.language.startsWith('en');
        const text = isEnglish && record.translatedToEnglish?.problem
          ? record.translatedToEnglish.problem
          : record.content.problem;

        return (
          <Tooltip title={text}>
            <div style={{
              maxWidth: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: t('guides.solutions'),
      key: 'solutions',
      width: 140,
      render: (_, record) => {
        const count = record.content.solutions?.length || 0;
        return (
          <Space>
            <span>{count}</span>
            {count > 0 && (
              <Tooltip title={t('guides.viewSolutions')}>
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSolutionsModalData({ problem: record.content.problem, solutions: record.content.solutions || [] });
                    setSolutionsModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: t('guides.filters'),
      key: 'filters',
      width: 400,
      render: (_, record) => {
        const count = record.filters?.length || 0;
        return (
          <Space size={8} align="center">
            <FilterPills filters={record.filters || []} maxVisible={4} />
            {count > 0 && (
              <Tooltip title={t('guides.viewFilters')}>
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFiltersModalData(record.filters || []);
                    setFiltersModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: t('guides.statusColumn'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      sortDirections: ['descend', 'ascend', 'descend'],
      sortOrder: sortField === 'status' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : undefined,
      showSorterTooltip: false,
      render: (status: string) => {
        const statusKey = status.toLowerCase();
        return (
          <Tag color={getStatusColor(status)}>
            {t(`guides.status.${statusKey}`)}
          </Tag>
        );
      },
    },
    {
      title: t('guides.created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      sorter: true,
      sortDirections: ['descend', 'ascend', 'descend'],
      sortOrder: sortField === 'createdAt' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : undefined,
      showSorterTooltip: false,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: t('guides.updated'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 120,
      sorter: true,
      sortDirections: ['descend', 'ascend', 'descend'],
      sortOrder: sortField === 'updatedAt' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : undefined,
      showSorterTooltip: false,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: t('guides.actions'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Tooltip title={t('common.delete')}>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={(e) => { e.stopPropagation(); handleDeleteGuide(record.id!); }}
          />
        </Tooltip>
      ),
    },
  ];

  const tableData: GuidesTableData[] = (guidesResponse?.guides || []).map(guide => ({
    ...guide,
    key: guide.id || Math.random().toString(),
  }));

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: any,
    sorter: SorterResult<GuidesTableData> | SorterResult<GuidesTableData>[]
  ) => {
    // Handle pagination
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);

    // Handle sorting - only process single column sort
    if (!Array.isArray(sorter) && sorter.columnKey) {
      const field = sorter.columnKey as SortField;

      // Ant Design's sortOrder in the sorter object: 'ascend' | 'descend' | undefined
      // Map Ant Design's sort order to our format
      if (sorter.order === 'ascend') {
        setSortField(field);
        setSortOrder('asc');
      } else if (sorter.order === 'descend') {
        setSortField(field);
        setSortOrder('desc');
      } else {
        // No sort order means we've cycled back - keep the field but reset to desc
        setSortField(field);
        setSortOrder('desc');
      }
    }
  };

  return (
    <>
      {contextHolder}
      <div>
        <PageTitle
          title={t('guides.title')}
          subtitle={t('guides.subtitle')}
        />

        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/guides/new')}
          >
            {t('guides.createGuide')}
          </Button>
        </div>
        
        <Table
          columns={columns}
          dataSource={tableData}
          loading={isLoading}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => {
              if (record.id) {
                navigate(`/guides/${record.id}`);
              }
            },
            style: { cursor: 'pointer' }
          })}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: guidesResponse?.totalCount || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              t('guides.totalGuides', { start: range[0], end: range[1], total }),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 1200 }}
        />

        <Modal
          open={solutionsModalOpen}
          title={solutionsModalData?.problem}
          footer={null}
          onCancel={() => {
            setSolutionsModalOpen(false);
            setSolutionsModalData(null);
          }}
        >
          <ul style={{ paddingLeft: 20 }}>
            {(solutionsModalData?.solutions || []).map((solution, index) => (
              <li key={index}>
                {solution}
              </li>
            ))}
          </ul>
        </Modal>

        <Modal
          open={filtersModalOpen}
          title={t('guides.filters')}
          footer={null}
          onCancel={() => {
            setFiltersModalOpen(false);
            setFiltersModalData([]);
          }}
          width={800}
        >
          <Table
            dataSource={(filtersModalData || []).map((f, idx) => ({ ...f, key: String(idx) }))}
            pagination={false}
            size="small"
            columns={[
              { title: t('guides.manufacturer'), dataIndex: 'manufacturer', key: 'manufacturer', width: 150 },
              { title: t('guides.modelSeries'), dataIndex: 'modelSeries', key: 'modelSeries', width: 150 },
              { title: t('guides.engine'), dataIndex: 'engineBaseType', key: 'engineBaseType', width: 120, render: (engine: EngineType) => (
                <Tag color={getEngineTypeColor(engine)}>{engine}</Tag>
              ) },
              { title: t('guides.eurocode'), dataIndex: 'euroCode', key: 'euroCode', width: 120 },
              { title: t('guides.modelYearMin'), dataIndex: 'modelYearMin', key: 'modelYearMin', width: 120 },
              { title: t('guides.modelYearMax'), dataIndex: 'modelYearMax', key: 'modelYearMax', width: 120 },
            ]}
          />
        </Modal>
      </div>
    </>
  );
};